<script setup lang="ts">
/**
 * DevConsolePanel — Developer-only error console flyout.
 * Shows a thin bottom bar with error count; expands to a resizable panel.
 * Rendered as a flex-shrink-0 child of #app so main content stops above it.
 */
import { onMounted, ref, watch } from 'vue'
import {
  useDevConsole,
  type ConsoleEntryType,
  type ConsoleEntrySource,
} from '@/composables/useDevConsole'
import { useUserPreferencesStore } from '@/stores/user-preferences.store'
import {
  AlertTriangle,
  Bug,
  Wifi,
  XCircle,
  Trash2,
  Copy,
  ChevronDown,
  ChevronUp,
  Filter,
} from 'lucide-vue-next'

const {
  entries,
  errorCount,
  filters,
  isExpanded,
  install,
  clear,
  toggleFilter,
  copyEntry,
  copyAll,
} = useDevConsole()

const panelHeight = ref(35) // percentage of viewport

const prefs = useUserPreferencesStore()

onMounted(() => {
  if (prefs.devConsoleVisible) install()
})

watch(
  () => prefs.devConsoleVisible,
  visible => {
    if (visible) install()
  }
)

function startResize(e: MouseEvent) {
  e.preventDefault()
  const onMove = (ev: MouseEvent) => {
    const vh = window.innerHeight
    const newHeight = ((vh - ev.clientY) / vh) * 100
    panelHeight.value = Math.max(15, Math.min(80, newHeight))
  }
  const onUp = () => {
    document.removeEventListener('mousemove', onMove)
    document.removeEventListener('mouseup', onUp)
  }
  document.addEventListener('mousemove', onMove)
  document.addEventListener('mouseup', onUp)
}

async function handleCopyAll() {
  const text = copyAll()
  await navigator.clipboard.writeText(text)
}

async function handleCopyEntry(entry: Parameters<typeof copyEntry>[0]) {
  const text = copyEntry(entry)
  await navigator.clipboard.writeText(text)
}

const typeIcons: Record<ConsoleEntryType, typeof AlertTriangle> = {
  'console-error': XCircle,
  exception: Bug,
  rejection: AlertTriangle,
  network: Wifi,
}

const typeLabels: Record<ConsoleEntryType, string> = {
  'console-error': 'Console',
  exception: 'Exception',
  rejection: 'Rejection',
  network: 'Network',
}

const typeColors: Record<ConsoleEntryType, string> = {
  'console-error': 'text-red-400',
  exception: 'text-orange-400',
  rejection: 'text-yellow-400',
  network: 'text-purple-400',
}

const sourceColors: Record<ConsoleEntrySource, string> = {
  Frontend: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  BFF: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  Database: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
}
</script>

<template>
  <div v-if="prefs.devConsoleVisible" class="flex-shrink-0 z-[60]">
    <!-- Resize handle (only when expanded) -->
    <div
      v-if="isExpanded"
      class="h-2.5 cursor-ns-resize bg-gray-200 dark:bg-charcoal-700 hover:bg-blue-400 dark:hover:bg-blue-500 transition-colors flex items-center justify-center"
      @mousedown="startResize"
    >
      <div class="w-10 h-1 rounded-full bg-gray-400 dark:bg-gray-500" />
    </div>

    <!-- Expanded panel -->
    <div
      v-if="isExpanded"
      class="bg-white dark:bg-charcoal-900 border-t border-gray-200 dark:border-white/10 flex flex-col overflow-hidden"
      :style="{ height: `${panelHeight}vh` }"
    >
      <!-- Toolbar -->
      <div
        class="flex items-center gap-2 px-3 py-1.5 border-b border-gray-100 dark:border-white/10 bg-gray-50 dark:bg-charcoal-800 flex-shrink-0"
      >
        <!-- Filters -->
        <div class="flex items-center gap-1">
          <Filter :size="12" class="text-gray-400 mr-1" />
          <button
            v-for="filterType in [
              'console-error',
              'exception',
              'rejection',
              'network',
            ] as ConsoleEntryType[]"
            :key="filterType"
            class="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-mono transition-all"
            :class="
              filters[filterType]
                ? 'bg-gray-200 dark:bg-white/10 text-gray-700 dark:text-gray-200'
                : 'text-gray-400 dark:text-gray-500 opacity-50'
            "
            :title="`Toggle ${typeLabels[filterType]} errors`"
            @click="toggleFilter(filterType)"
          >
            <component :is="typeIcons[filterType]" :size="11" />
            <span class="hidden sm:inline">{{ typeLabels[filterType] }}</span>
          </button>
        </div>

        <div class="flex-1" />

        <!-- Actions -->
        <button
          class="flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
          title="Copy all visible entries"
          @click="handleCopyAll"
        >
          <Copy :size="11" />
          <span class="hidden sm:inline">Copy All</span>
        </button>
        <button
          class="flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
          title="Clear all entries"
          @click="clear"
        >
          <Trash2 :size="11" />
          <span class="hidden sm:inline">Clear</span>
        </button>
        <button
          class="flex items-center gap-1 px-2 py-0.5 rounded text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
          title="Collapse console"
          @click="isExpanded = false"
        >
          <ChevronDown :size="12" />
        </button>
      </div>

      <!-- Entries list -->
      <div class="flex-1 overflow-y-auto font-mono text-xs">
        <div v-if="!entries.length" class="flex items-center justify-center h-full text-gray-400">
          No errors captured
        </div>
        <div
          v-for="entry in entries"
          :key="entry.id"
          class="group flex items-center gap-2 px-3 py-1.5 border-b border-gray-50 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/5"
        >
          <component
            :is="typeIcons[entry.type]"
            :size="12"
            class="flex-shrink-0"
            :class="typeColors[entry.type]"
          />
          <span
            class="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold leading-none"
            :class="sourceColors[entry.source]"
          >
            {{ entry.source }}
          </span>
          <span class="text-gray-400 flex-shrink-0 whitespace-nowrap">
            {{ entry.timestamp.toLocaleTimeString() }}
          </span>
          <span class="flex-1 text-gray-700 dark:text-gray-200 truncate">
            {{ entry.message }}<template v-if="entry.url"> | {{ entry.url }}</template>
          </span>
          <button
            class="flex-shrink-0 opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-gray-200 dark:hover:bg-white/10 transition-all text-gray-400"
            title="Copy entry"
            @click="handleCopyEntry(entry)"
          >
            <Copy :size="11" />
          </button>
        </div>
      </div>
    </div>

    <!-- Collapsed bar -->
    <div
      v-else
      class="flex items-center justify-between px-3 py-1 bg-gray-100 dark:bg-charcoal-800 border-t border-gray-200 dark:border-white/10 cursor-pointer hover:bg-gray-200 dark:hover:bg-charcoal-700 transition-colors"
      @click="isExpanded = true"
    >
      <div class="flex items-center gap-2">
        <Bug :size="12" class="text-blue-400" />
        <span class="hidden sm:inline text-xs font-mono text-gray-500 dark:text-gray-400"
          >Dev Console</span
        >
        <span
          v-if="errorCount > 0"
          class="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold"
        >
          {{ errorCount > 99 ? '99+' : errorCount }}
        </span>
      </div>
      <ChevronUp :size="12" class="text-gray-400" />
    </div>
  </div>
</template>
